import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import {
  Settings, Palette, Users, CreditCard, Shield, FileText,
  Save, Plus, Trash2, Eye, EyeOff, Copy, RefreshCw,
  Globe, Mail, Hash, DollarSign, Percent, ToggleLeft, ToggleRight,
  AlertTriangle, Check, X, ExternalLink, Lock, Key, Ban,
  Zap, Building2, Landmark, Send, ChevronDown, ChevronUp,
  Paintbrush, Type, Image, Link2, MonitorSmartphone, Video, Upload, Play, Link, Unlink,
  Search, UserPlus, UserCheck, UserX, Activity, Clock, Filter,
  Wifi, WifiOff, TestTube, ChevronRight, GripVertical,
  Calendar, FileUp, SlidersHorizontal, Layers, Database,
  BarChart3, ShieldCheck, ArrowUpDown, MoreHorizontal,
  CheckCircle2, XCircle, AlertCircle, Info, Sparkles,
  Smartphone, CreditCard as CardIcon, Building, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useTheme } from '../App';
import api, { facebookAPI, adminAPI } from '../lib/api';

// ─── Animation Variants ───────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] } }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.06 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

const slideIn = {
  hidden: { opacity: 0, x: -20 },
  visible: (i = 0) => ({ opacity: 1, x: 0, transition: { delay: i * 0.04, duration: 0.4, ease: [0.22, 1, 0.36, 1] } }),
};

// ─── Reusable Helpers ─────────────────────────────────────────────
function SectionTitle({ icon: Icon, title, subtitle, isDark }) {
  return (
    <div className="flex items-start gap-3 mb-6">
      <div className={`p-2.5 rounded-xl ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
        <Icon className="w-5 h-5 text-blue-500" />
      </div>
      <div>
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>{title}</h3>
        {subtitle && <p className={`text-sm mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{subtitle}</p>}
      </div>
    </div>
  );
}

function FieldGroup({ label, description, children, isDark }) {
  return (
    <div className="space-y-1.5">
      <label className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{label}</label>
      {description && <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{description}</p>}
      {children}
    </div>
  );
}

function ToggleSwitch({ enabled, onToggle, isDark }) {
  return (
    <button onClick={onToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${enabled ? 'bg-blue-500' : isDark ? 'bg-white/10' : 'bg-gray-300'}`}>
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className={`inline-block h-4 w-4 transform rounded-full bg-white ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
}

function SaveButton({ onClick, loading, isDark, label = 'حفظ التغييرات' }) {
  return (
    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
      <Button
        onClick={onClick}
        disabled={loading}
        className={`rounded-xl px-6 h-10 font-medium transition-all ${
          isDark
            ? 'bg-blue-500 hover:bg-blue-400 text-white shadow-lg shadow-blue-500/20'
            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-600/20'
        }`}
      >
        {loading ? <RefreshCw className="w-4 h-4 animate-spin ml-2" /> : <Save className="w-4 h-4 ml-2" />}
        {label}
      </Button>
    </motion.div>
  );
}

function GlassCard({ children, isDark, className = '', hover = true }) {
  return (
    <motion.div
      whileHover={hover ? { y: -2, transition: { duration: 0.2 } } : {}}
      className={`rounded-2xl p-6 sm:p-8 backdrop-blur-xl transition-all duration-300 ${
        isDark
          ? 'bg-[#0A0A0C]/60 border border-white/[0.05] hover:border-white/[0.1]'
          : 'bg-white/80 border border-gray-200/80 shadow-sm hover:shadow-md'
      } ${className}`}
    >
      {children}
    </motion.div>
  );
}

function StatusDot({ active, isDark }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      {active && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${active ? 'bg-emerald-500' : isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
    </span>
  );
}

function RoleBadge({ role, isDark }) {
  const roleStyles = {
    admin: isDark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-600 border-red-200',
    authority: isDark ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-200',
    affiliate: isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200',
    subscriber: isDark ? 'bg-white/[0.04] text-white/40 border-white/[0.06]' : 'bg-gray-100 text-gray-500 border-gray-200',
  };
  const roleLabels = { admin: 'مدير', authority: 'جهة مختصة', affiliate: 'مسوق', subscriber: 'مشترك' };
  return (
    <Badge className={`text-[10px] ${roleStyles[role] || roleStyles.subscriber}`}>
      {roleLabels[role] || role}
    </Badge>
  );
}

function EmptyState({ icon: Icon, title, subtitle, isDark }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`text-center py-12 rounded-2xl border-2 border-dashed ${isDark ? 'border-white/[0.04] text-white/20' : 'border-gray-200 text-gray-400'}`}
    >
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p className={`text-sm font-medium ${isDark ? 'text-white/30' : 'text-gray-500'}`}>{title}</p>
      {subtitle && <p className={`text-xs mt-1 ${isDark ? 'text-white/15' : 'text-gray-400'}`}>{subtitle}</p>}
    </motion.div>
  );
}

// ─── Tab Content Components ───────────────────────────────────────

// TAB: General
function GeneralTab({ isDark, card, inputCl, textP, textS }) {
  const [settings, setSettings] = useState({ site_name: '', support_email: '', arbitration_email: '', max_free_appeals: 1 });
  const [loading, setLoading] = useState(false);
  const [siteTheme, setSiteTheme] = useState('midnight');
  const [themeLoading, setThemeLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settings');
        const d = res.data;
        setSettings({ site_name: d.site_name || '', support_email: d.support_email || '', arbitration_email: d.arbitration_email || '', max_free_appeals: d.max_free_appeals ?? 1 });
      } catch { toast.error('فشل تحميل الإعدادات'); }
      try {
        const themeRes = await api.get('/settings/public-theme');
        if (themeRes.data.theme) setSiteTheme(themeRes.data.theme);
      } catch {}
    })();
  }, []);

  const save = async () => {
    setLoading(true);
    try { await api.put('/admin/settings', settings); toast.success('تم حفظ الإعدادات العامة'); }
    catch { toast.error('فشل حفظ الإعدادات'); }
    finally { setLoading(false); }
  };

  const saveTheme = async (newTheme) => {
    setSiteTheme(newTheme);
    setThemeLoading(true);
    try { await api.put('/settings', { key: 'site_theme', value: newTheme }); toast.success('تم تحديث المظهر! أعد تحميل الصفحة لرؤية التغييرات.'); }
    catch { toast.error('فشل حفظ المظهر'); }
    finally { setThemeLoading(false); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      <motion.div variants={fadeUp}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Globe} title="الإعدادات العامة" subtitle="إعدادات المنصة الأساسية والافتراضية" isDark={isDark} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldGroup label="اسم الموقع" description="يظهر في علامة تبويب المتصفح والبريد" isDark={isDark}>
              <Input value={settings.site_name} onChange={e => setSettings(p => ({ ...p, site_name: e.target.value }))} placeholder="myAct" className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
            <FieldGroup label="بريد الدعم" description="عنوان بريد الدعم للعملاء" isDark={isDark}>
              <Input value={settings.support_email} onChange={e => setSettings(p => ({ ...p, support_email: e.target.value }))} placeholder="support@myact.com" className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
            <FieldGroup label="بريد التحكيم" description="يتم توجيه طلبات التحكيم إليه" isDark={isDark}>
              <Input value={settings.arbitration_email} onChange={e => setSettings(p => ({ ...p, arbitration_email: e.target.value }))} placeholder="arbitration@myact.com" className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
            <FieldGroup label="الاستئنافات المجانية" description="عدد الاستئنافات المجانية لكل مستخدم" isDark={isDark}>
              <Input type="number" min={0} value={settings.max_free_appeals} onChange={e => setSettings(p => ({ ...p, max_free_appeals: parseInt(e.target.value) || 0 }))} className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
          </div>
          <div className="flex justify-end mt-8">
            <SaveButton onClick={save} loading={loading} isDark={isDark} />
          </div>
        </GlassCard>
      </motion.div>

      <motion.div variants={fadeUp} custom={1}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Palette} title="مظهر الموقع" subtitle="اختر المظهر العام لجميع الزوار (المدير فقط)" isDark={isDark} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {[
              { id: 'midnight', label: 'سديم منتصف الليل', icon: Sparkles, colors: ['#020204', '#3B82F6', '#6366F1'] },
              { id: 'aurora', label: 'شفق جليدي', icon: Sparkles, colors: ['#F8FAFC', '#3B82F6', '#06B6D4'] },
              { id: 'cyber', label: 'نيون سايبر', icon: Zap, colors: ['#0A0F1C', '#00FF88', '#A855F7'] },
              { id: 'ember', label: 'غروب ملتهب', icon: Zap, colors: ['#1A0F0A', '#F97316', '#FB7185'] },
            ].map(t => (
              <motion.button key={t.id} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => saveTheme(t.id)} disabled={themeLoading}
                className={`relative p-4 rounded-xl border text-right transition-all ${
                  siteTheme === t.id
                    ? isDark ? 'border-blue-500/50 bg-blue-500/10 ring-2 ring-blue-500/30' : 'border-blue-400 bg-blue-50 ring-2 ring-blue-400/30'
                    : isDark ? 'border-white/[0.06] hover:border-white/[0.15] bg-white/[0.02]' : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}>
                {siteTheme === t.id && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-2 left-2 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </motion.div>
                )}
                <t.icon className={`w-5 h-5 mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                <div className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.label}</div>
                <div className="flex gap-1.5">
                  {t.colors.map((c, i) => (
                    <div key={i} className="w-5 h-5 rounded-full border border-white/20" style={{ backgroundColor: c }} />
                  ))}
                </div>
              </motion.button>
            ))}
          </div>
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}

// TAB: Branding
function BrandingTab({ isDark, card, inputCl, textP, textS }) {
  const [branding, setBranding] = useState({ site_name: '', site_tagline: '', primary_color: '#3B82F6', accent_color: '#06B6D4', logo_url: '', favicon_url: '', footer_text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get('/admin/branding'); setBranding(prev => ({ ...prev, ...res.data })); }
      catch { toast.error('فشل تحميل بيانات العلامة التجارية'); }
    })();
  }, []);

  const save = async () => {
    setLoading(true);
    try { await api.put('/admin/branding', branding); toast.success('تم تحديث العلامة التجارية'); }
    catch { toast.error('فشل حفظ العلامة التجارية'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <motion.div variants={fadeUp} className="lg:col-span-3">
          <GlassCard isDark={isDark}>
            <SectionTitle icon={Paintbrush} title="هوية العلامة التجارية" subtitle="خصص مظهر وشعور المنصة" isDark={isDark} />
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FieldGroup label="اسم الموقع" isDark={isDark}>
                  <Input value={branding.site_name} onChange={e => setBranding(p => ({ ...p, site_name: e.target.value }))} placeholder="myAct" className={`rounded-xl h-10 ${inputCl}`} />
                </FieldGroup>
                <FieldGroup label="الشعار النصي" isDark={isDark}>
                  <Input value={branding.site_tagline} onChange={e => setBranding(p => ({ ...p, site_tagline: e.target.value }))} placeholder="حقوقك، مهمتنا" className={`rounded-xl h-10 ${inputCl}`} />
                </FieldGroup>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FieldGroup label="اللون الرئيسي" isDark={isDark}>
                  <div className="flex items-center gap-3">
                    <input type="color" value={branding.primary_color} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} className="w-10 h-10 rounded-xl border-0 cursor-pointer bg-transparent" />
                    <Input value={branding.primary_color} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} className={`rounded-xl h-10 font-mono text-sm ${inputCl}`} />
                  </div>
                </FieldGroup>
                <FieldGroup label="اللون الثانوي" isDark={isDark}>
                  <div className="flex items-center gap-3">
                    <input type="color" value={branding.accent_color} onChange={e => setBranding(p => ({ ...p, accent_color: e.target.value }))} className="w-10 h-10 rounded-xl border-0 cursor-pointer bg-transparent" />
                    <Input value={branding.accent_color} onChange={e => setBranding(p => ({ ...p, accent_color: e.target.value }))} className={`rounded-xl h-10 font-mono text-sm ${inputCl}`} />
                  </div>
                </FieldGroup>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FieldGroup label="رابط الشعار" description="رابط URL الكامل لصورة الشعار" isDark={isDark}>
                  <Input value={branding.logo_url} onChange={e => setBranding(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." className={`rounded-xl h-10 ${inputCl}`} />
                </FieldGroup>
                <FieldGroup label="رابط الأيقونة" description="أيقونة 16x16 أو 32x32" isDark={isDark}>
                  <Input value={branding.favicon_url} onChange={e => setBranding(p => ({ ...p, favicon_url: e.target.value }))} placeholder="https://..." className={`rounded-xl h-10 ${inputCl}`} />
                </FieldGroup>
              </div>
              <FieldGroup label="نص التذييل" isDark={isDark}>
                <Input value={branding.footer_text} onChange={e => setBranding(p => ({ ...p, footer_text: e.target.value }))} placeholder="© 2026 myAct. جميع الحقوق محفوظة." className={`rounded-xl h-10 ${inputCl}`} />
              </FieldGroup>
            </div>
            <div className="flex justify-end mt-8">
              <SaveButton onClick={save} loading={loading} isDark={isDark} />
            </div>
          </GlassCard>
        </motion.div>

        <motion.div variants={fadeUp} custom={2} className="lg:col-span-2 space-y-4">
          <GlassCard isDark={isDark} hover={false}>
            <div className="flex items-center gap-2 mb-5">
              <MonitorSmartphone className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              <span className={`text-sm font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`}>معاينة مباشرة</span>
            </div>
            <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-white/[0.06]' : 'border-gray-200'}`}>
              <div className={`flex items-center gap-2 px-3 py-2 ${isDark ? 'bg-white/[0.03]' : 'bg-gray-100'}`}>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                </div>
                <div className={`flex-1 text-center text-[10px] px-3 py-0.5 rounded ${isDark ? 'bg-white/[0.04] text-white/30' : 'bg-white text-gray-400'}`}>
                  {branding.site_name || 'myAct'}.com
                </div>
              </div>
              <div className="p-4" style={{ backgroundColor: branding.primary_color + '10' }}>
                <div className="flex items-center gap-2 mb-3">
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="" className="w-6 h-6 rounded object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: branding.primary_color + '30' }} />
                  )}
                  <span className="text-sm font-bold" style={{ color: branding.primary_color, fontFamily: 'Outfit' }}>
                    {branding.site_name || 'myAct'}
                  </span>
                </div>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{branding.site_tagline || 'الشعار النصي هنا'}</p>
              </div>
              <div className={`p-4 space-y-2 ${isDark ? 'bg-[#0A0A0C]/80' : 'bg-white'}`}>
                <div className="h-2 rounded-full w-3/4" style={{ backgroundColor: branding.primary_color + '20' }} />
                <div className="h-2 rounded-full w-1/2" style={{ backgroundColor: branding.accent_color + '20' }} />
                <div className="flex gap-2 mt-3">
                  <div className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-white" style={{ backgroundColor: branding.primary_color }}>رئيسي</div>
                  <div className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-white" style={{ backgroundColor: branding.accent_color }}>ثانوي</div>
                </div>
              </div>
              <div className={`px-4 py-2 text-[9px] ${isDark ? 'bg-white/[0.02] text-white/20' : 'bg-gray-50 text-gray-400'}`}>
                {branding.footer_text || '© 2026 myAct'}
              </div>
            </div>
          </GlassCard>

          <GlassCard isDark={isDark} hover={false}>
            <p className={`text-xs font-medium mb-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>لوحة الألوان</p>
            <div className="grid grid-cols-2 gap-3">
              {[{ color: branding.primary_color, label: 'رئيسي' }, { color: branding.accent_color, label: 'ثانوي' }].map((c, i) => (
                <div key={i} className="space-y-1.5">
                  <motion.div whileHover={{ scale: 1.05 }} className="h-12 rounded-xl cursor-pointer" style={{ backgroundColor: c.color }} />
                  <p className={`text-[10px] font-mono text-center ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{c.color}</p>
                </div>
              ))}
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </motion.div>
  );
}

// TAB: Affiliate
function AffiliateTab({ isDark, card, inputCl, textP, textS }) {
  const [affiliate, setAffiliate] = useState({ affiliate_commission: 20, affiliate_min_payout: 50, affiliate_active: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settings');
        const d = res.data;
        setAffiliate({ affiliate_commission: d.affiliate_commission ?? 20, affiliate_min_payout: d.affiliate_min_payout ?? 50, affiliate_active: d.affiliate_active ?? true });
      } catch {}
    })();
  }, []);

  const save = async () => {
    setLoading(true);
    try { await api.put('/admin/settings', affiliate); toast.success('تم حفظ إعدادات الأفليت'); }
    catch { toast.error('فشل حفظ إعدادات الأفليت'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      <motion.div variants={fadeUp}>
        <div className={`rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
          affiliate.affiliate_active
            ? isDark ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-emerald-50 border border-emerald-200'
            : isDark ? 'bg-red-500/5 border border-red-500/10' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            <motion.div animate={{ rotate: affiliate.affiliate_active ? 0 : 180 }} className={`p-2 rounded-xl ${affiliate.affiliate_active ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              {affiliate.affiliate_active ? <Check className="w-5 h-5 text-emerald-500" /> : <X className="w-5 h-5 text-red-500" />}
            </motion.div>
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
                برنامج الأفليت {affiliate.affiliate_active ? 'نشط' : 'معطل'}
              </p>
              <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                {affiliate.affiliate_active ? 'يمكن للشركاء كسب عمولات على الإحالات' : 'لا يتم معالجة أي عمولات'}
              </p>
            </div>
          </div>
          <ToggleSwitch enabled={affiliate.affiliate_active} onToggle={() => setAffiliate(p => ({ ...p, affiliate_active: !p.affiliate_active }))} isDark={isDark} />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[
          { label: 'نسبة العمولة', value: `${affiliate.affiliate_commission}%`, sub: 'لكل استئناف محال', icon: Percent, color: 'blue' },
          { label: 'الحد الأدنى للسحب', value: `$${affiliate.affiliate_min_payout}`, sub: 'الحد الأدنى للسحب', icon: DollarSign, color: 'emerald' },
          { label: 'حالة البرنامج', value: affiliate.affiliate_active ? 'نشط' : 'متوقف', sub: affiliate.affiliate_active ? 'الكسب مفعل' : 'الكسب معطل', icon: Zap, color: affiliate.affiliate_active ? 'amber' : 'red' },
        ].map((stat, i) => (
          <motion.div key={stat.label} variants={fadeUp} custom={i + 1}>
            <GlassCard isDark={isDark}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-xl ${isDark ? `bg-${stat.color}-500/10` : `bg-${stat.color}-50`}`}>
                  <stat.icon className={`w-4 h-4 text-${stat.color}-500`} />
                </div>
                <Badge className={`text-[10px] ${isDark ? 'bg-white/[0.04] text-white/40 border-white/[0.06]' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>{stat.label}</Badge>
              </div>
              <p className={`text-2xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{stat.sub}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <motion.div variants={fadeUp} custom={4}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Users} title="إعدادات العمولة" subtitle="تكوين كيفية كسب الأفلييت والدفع لهم" isDark={isDark} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldGroup label="نسبة العمولة (%)" description="النسبة المئوية من تكلفة الاستئناف كعمولة" isDark={isDark}>
              <div className="relative">
                <Input type="number" min={0} max={100} value={affiliate.affiliate_commission} onChange={e => setAffiliate(p => ({ ...p, affiliate_commission: parseFloat(e.target.value) || 0 }))} className={`rounded-xl h-10 pl-10 ${inputCl}`} />
                <Percent className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/20' : 'text-gray-400'}`} />
              </div>
            </FieldGroup>
            <FieldGroup label="الحد الأدنى للسحب ($)" description="الحد الأدنى للرصيد المطلوب للسحب" isDark={isDark}>
              <div className="relative">
                <Input type="number" min={0} value={affiliate.affiliate_min_payout} onChange={e => setAffiliate(p => ({ ...p, affiliate_min_payout: parseFloat(e.target.value) || 0 }))} className={`rounded-xl h-10 pl-10 ${inputCl}`} />
                <DollarSign className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/20' : 'text-gray-400'}`} />
              </div>
            </FieldGroup>
          </div>
          <div className="flex justify-end mt-8">
            <SaveButton onClick={save} loading={loading} isDark={isDark} />
          </div>
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}

// TAB: Payment Gateways
function PaymentsTab({ isDark, card, inputCl, textP, textS }) {
  const [gateways, setGateways] = useState({
    stripe: { enabled: false, api_key: '', publishable_key: '' },
    paypal: { enabled: false, client_id: '', client_secret: '', mode: 'sandbox' },
    bank_transfer: { enabled: false, bank_name: '', iban: '', swift: '', account_holder: '' },
    western_union: { enabled: false, receiver_name: '', country: '', instructions: '' },
  });
  const [gatewayLoading, setGatewayLoading] = useState({});
  const [expandedGateway, setExpandedGateway] = useState('stripe');
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    (async () => {
      const ids = ['stripe', 'paypal', 'bank_transfer', 'western_union'];
      try {
        const results = await Promise.allSettled(ids.map(id => api.get(`/admin/payment-gateways/${id}`)));
        const updated = { ...gateways };
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value.data) updated[ids[i]] = { ...updated[ids[i]], ...r.value.data };
        });
        setGateways(updated);
      } catch { toast.error('فشل تحميل بوابات الدفع'); }
    })();
  // eslint-disable-next-line
  }, []);

  const saveGateway = async (id) => {
    setGatewayLoading(prev => ({ ...prev, [id]: true }));
    try { await api.put(`/admin/payment-gateways/${id}`, gateways[id]); toast.success(`تم تحديث بوابة ${id.replace('_', ' ')}`); }
    catch { toast.error('فشل حفظ البوابة'); }
    finally { setGatewayLoading(prev => ({ ...prev, [id]: false })); }
  };

  const gatewayMeta = {
    stripe: { label: 'Stripe', icon: CreditCard, color: 'purple', fields: [
      { key: 'api_key', label: 'مفتاح API السري', placeholder: 'sk_live_...', secret: true },
      { key: 'publishable_key', label: 'المفتاح العام', placeholder: 'pk_live_...' },
    ]},
    paypal: { label: 'PayPal', icon: DollarSign, color: 'blue', fields: [
      { key: 'client_id', label: 'معرف العميل', placeholder: 'AX...' },
      { key: 'client_secret', label: 'مفتاح العميل السري', placeholder: 'EL...', secret: true },
      { key: 'mode', label: 'الوضع', type: 'select', options: ['sandbox', 'live'] },
    ]},
    bank_transfer: { label: 'تحويل بنكي', icon: Landmark, color: 'emerald', fields: [
      { key: 'bank_name', label: 'اسم البنك', placeholder: 'مثال: البنك الأهلي' },
      { key: 'iban', label: 'رقم IBAN', placeholder: 'SA...' },
      { key: 'swift', label: 'SWIFT/BIC', placeholder: 'NCBKSAJE' },
      { key: 'account_holder', label: 'صاحب الحساب', placeholder: 'اسم الشركة' },
    ]},
    western_union: { label: 'ويسترن يونيون', icon: Send, color: 'amber', fields: [
      { key: 'receiver_name', label: 'اسم المستلم', placeholder: 'الاسم الكامل' },
      { key: 'country', label: 'الدولة', placeholder: 'مثال: السعودية' },
      { key: 'instructions', label: 'التعليمات', type: 'textarea', placeholder: 'تعليمات إضافية للتحويل...' },
    ]},
  };

  const colorMap = {
    purple: { bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50', border: isDark ? 'border-purple-500/20' : 'border-purple-200', text: 'text-purple-500' },
    blue: { bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50', border: isDark ? 'border-blue-500/20' : 'border-blue-200', text: 'text-blue-500' },
    emerald: { bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', border: isDark ? 'border-emerald-500/20' : 'border-emerald-200', text: 'text-emerald-500' },
    amber: { bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50', border: isDark ? 'border-amber-500/20' : 'border-amber-200', text: 'text-amber-500' },
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-4">
      <motion.div variants={fadeUp}>
        <SectionTitle icon={CreditCard} title="بوابات الدفع" subtitle="تكوين كيفية دفع العملاء مقابل الخدمات" isDark={isDark} />
      </motion.div>

      {Object.entries(gatewayMeta).map(([id, meta], idx) => {
        const gw = gateways[id];
        const cm = colorMap[meta.color];
        const isExpanded = expandedGateway === id;

        return (
          <motion.div key={id} variants={fadeUp} custom={idx + 1}>
            <GlassCard isDark={isDark} hover={false} className="!p-0 overflow-hidden">
              <div className="flex items-center justify-between p-5 cursor-pointer" onClick={() => setExpandedGateway(isExpanded ? null : id)}>
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${cm.bg} border ${cm.border}`}>
                    <meta.icon className={`w-5 h-5 ${cm.text}`} />
                  </div>
                  <div>
                    <p className={`font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{meta.label}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusDot active={gw.enabled} isDark={isDark} />
                      <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                        {gw.enabled ? 'نشط ويقبل المدفوعات' : 'معطل'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <ToggleSwitch enabled={gw.enabled} onToggle={() => setGateways(p => ({ ...p, [id]: { ...p[id], enabled: !p[id].enabled } }))} isDark={isDark} />
                  <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                    <ChevronDown className={`w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
                  </motion.div>
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                    <div className={`px-5 pb-5 pt-2 border-t ${isDark ? 'border-white/[0.04]' : 'border-gray-100'}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
                        {meta.fields.map(field => (
                          <FieldGroup key={field.key} label={field.label} isDark={isDark}>
                            {field.type === 'select' ? (
                              <select value={gw[field.key] || field.options[0]} onChange={e => setGateways(p => ({ ...p, [id]: { ...p[id], [field.key]: e.target.value } }))}
                                className={`flex h-10 w-full rounded-xl border px-3 py-1 text-sm transition-colors focus-visible:outline-none ${inputCl}`}>
                                {field.options.map(opt => (
                                  <option key={opt} value={opt} className={isDark ? 'bg-[#0A0A0C] text-white' : 'bg-white text-gray-900'}>{opt === 'sandbox' ? 'تجريبي' : 'مباشر'}</option>
                                ))}
                              </select>
                            ) : field.type === 'textarea' ? (
                              <Textarea value={gw[field.key] || ''} onChange={e => setGateways(p => ({ ...p, [id]: { ...p[id], [field.key]: e.target.value } }))} placeholder={field.placeholder} rows={3} className={`rounded-xl ${inputCl}`} />
                            ) : (
                              <div className="relative">
                                <Input type={field.secret && !showSecrets[`${id}_${field.key}`] ? 'password' : 'text'} value={gw[field.key] || ''} onChange={e => setGateways(p => ({ ...p, [id]: { ...p[id], [field.key]: e.target.value } }))} placeholder={field.placeholder} className={`rounded-xl h-10 ${field.secret ? 'pl-10' : ''} ${inputCl}`} />
                                {field.secret && (
                                  <button onClick={() => setShowSecrets(p => ({ ...p, [`${id}_${field.key}`]: !p[`${id}_${field.key}`] }))} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/20 hover:text-white/40' : 'text-gray-400 hover:text-gray-600'}`}>
                                    {showSecrets[`${id}_${field.key}`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                )}
                              </div>
                            )}
                          </FieldGroup>
                        ))}
                      </div>
                      <div className="flex justify-end mt-6">
                        <SaveButton onClick={() => saveGateway(id)} loading={gatewayLoading[id]} isDark={isDark} label={`حفظ ${meta.label}`} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassCard>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

// TAB: Security
function SecurityTab({ isDark, card, inputCl, textP, textS }) {
  const [blocklist, setBlocklist] = useState([]);
  const [newBlock, setNewBlock] = useState({ type: 'email', value: '' });
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [showSecrets, setShowSecrets] = useState({});
  const [loading, setLoading] = useState(false);

  const loadBlocklist = useCallback(async () => {
    try { const res = await api.get('/admin/blocklist'); setBlocklist(Array.isArray(res.data) ? res.data : res.data.items || []); } catch {}
  }, []);

  const loadApiKeys = useCallback(async () => {
    try { const res = await api.get('/admin/api-keys'); setApiKeys(Array.isArray(res.data) ? res.data : res.data.keys || []); } catch {}
  }, []);

  useEffect(() => { loadBlocklist(); loadApiKeys(); }, [loadBlocklist, loadApiKeys]);

  const addBlocklistEntry = async () => {
    if (!newBlock.value.trim()) return;
    setLoading(true);
    try { await api.post('/admin/blocklist', newBlock); toast.success('تمت الإضافة للقائمة السوداء'); setNewBlock({ type: 'email', value: '' }); loadBlocklist(); }
    catch { toast.error('فشل إضافة العنصر'); }
    finally { setLoading(false); }
  };

  const removeBlocklistEntry = async (id) => {
    try { await api.delete(`/admin/blocklist/${id}`); toast.success('تم الحذف من القائمة السوداء'); loadBlocklist(); }
    catch { toast.error('فشل الحذف'); }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const res = await api.post('/admin/api-keys', { name: newKeyName });
      toast.success('تم إنشاء مفتاح API');
      setNewKeyName('');
      if (res.data.key) toast.info(`المفتاح: ${res.data.key}`, { duration: 10000 });
      loadApiKeys();
    } catch { toast.error('فشل إنشاء مفتاح API'); }
  };

  const deleteApiKey = async (id) => {
    try { await api.delete(`/admin/api-keys/${id}`); toast.success('تم إلغاء المفتاح'); loadApiKeys(); }
    catch { toast.error('فشل إلغاء المفتاح'); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      <motion.div variants={fadeUp}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Ban} title="قائمة الحظر" subtitle="حظر المستخدمين المسيئين عبر البريد أو عنوان IP" isDark={isDark} />
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <select value={newBlock.type} onChange={e => setNewBlock(p => ({ ...p, type: e.target.value }))} className={`h-10 rounded-xl border px-3 text-sm w-full sm:w-32 ${inputCl}`}>
              <option value="email" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>بريد إلكتروني</option>
              <option value="ip" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>عنوان IP</option>
            </select>
            <Input value={newBlock.value} onChange={e => setNewBlock(p => ({ ...p, value: e.target.value }))} placeholder={newBlock.type === 'email' ? 'user@spam.com' : '192.168.1.1'} className={`rounded-xl h-10 flex-1 ${inputCl}`} onKeyDown={e => e.key === 'Enter' && addBlocklistEntry()} />
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button onClick={addBlocklistEntry} disabled={loading} className={`rounded-xl h-10 px-5 ${isDark ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20' : 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200'}`}>
                <Plus className="w-4 h-4 ml-1.5" /> حظر
              </Button>
            </motion.div>
          </div>

          {blocklist.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto pl-1">
              {blocklist.map((entry, i) => (
                <motion.div key={entry.id || i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50 border border-gray-100'}`}>
                  <div className="flex items-center gap-3">
                    <Badge className={`text-[10px] ${entry.type === 'email' ? (isDark ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-purple-50 text-purple-600 border-purple-200') : (isDark ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-orange-50 text-orange-600 border-orange-200')}`}>
                      {entry.type === 'email' ? <Mail className="w-3 h-3 ml-1" /> : <Globe className="w-3 h-3 ml-1" />}
                      {entry.type?.toUpperCase()}
                    </Badge>
                    <span className={`text-sm font-mono ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{entry.value}</span>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeBlocklistEntry(entry.id)} className={`h-8 w-8 rounded-lg ${isDark ? 'hover:bg-red-500/10 text-white/30 hover:text-red-400' : 'hover:bg-red-50 text-gray-400 hover:text-red-600'}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </motion.div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Shield} title="لا يوجد عناصر محظورة بعد" isDark={isDark} />
          )}
        </GlassCard>
      </motion.div>

      <motion.div variants={fadeUp} custom={2}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Key} title="مفاتيح API" subtitle="إدارة رموز الوصول للتكاملات الخارجية" isDark={isDark} />
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="اسم المفتاح (مثال: تطبيق الجوال، Webhook)" className={`rounded-xl h-10 flex-1 ${inputCl}`} onKeyDown={e => e.key === 'Enter' && createApiKey()} />
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button onClick={createApiKey} className={`rounded-xl h-10 px-5 ${isDark ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/20' : 'bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200'}`}>
                <Plus className="w-4 h-4 ml-1.5" /> إنشاء مفتاح
              </Button>
            </motion.div>
          </div>

          {apiKeys.length > 0 ? (
            <div className="space-y-2">
              {apiKeys.map((key, i) => (
                <motion.div key={key.id || i} variants={slideIn} custom={i}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50 border border-gray-100'}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Lock className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-blue-400/60' : 'text-blue-500'}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-gray-800'}`}>{key.name}</p>
                      <p className={`text-xs font-mono truncate ${isDark ? 'text-white/20' : 'text-gray-400'}`}>{key.key_preview || key.key || '--------'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {key.created_at && <span className={`text-[10px] ${isDark ? 'text-white/20' : 'text-gray-400'}`}>{new Date(key.created_at).toLocaleDateString('ar')}</span>}
                    {key.key && (
                      <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(key.key); toast.success('تم النسخ'); }} className={`h-8 w-8 rounded-lg ${isDark ? 'hover:bg-white/5 text-white/30' : 'hover:bg-gray-100 text-gray-400'}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => deleteApiKey(key.id)} className={`h-8 w-8 rounded-lg ${isDark ? 'hover:bg-red-500/10 text-white/30 hover:text-red-400' : 'hover:bg-red-50 text-gray-400 hover:text-red-600'}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Key} title="لم يتم إنشاء مفاتيح API بعد" isDark={isDark} />
          )}
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}

// TAB: Pages Content
function PagesTab({ isDark, card, inputCl, textP, textS }) {
  const [pagesContent, setPagesContent] = useState({ about_content: '', terms_content: '', privacy_content: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settings');
        const d = res.data;
        setPagesContent({ about_content: d.about_content || '', terms_content: d.terms_content || '', privacy_content: d.privacy_content || '' });
      } catch {}
    })();
  }, []);

  const save = async () => {
    setLoading(true);
    try { await api.put('/admin/settings', pagesContent); toast.success('تم حفظ محتوى الصفحات'); }
    catch { toast.error('فشل حفظ محتوى الصفحات'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      <motion.div variants={fadeUp}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={FileText} title="محرر محتوى الصفحات" subtitle="تحرير المحتوى المعروض على الصفحات العامة" isDark={isDark} />
          <div className="space-y-8">
            {[
              { key: 'about_content', label: 'صفحة من نحن', description: 'يظهر في صفحة /about', icon: FileText },
              { key: 'terms_content', label: 'شروط الخدمة', description: 'الشروط والأحكام القانونية', icon: ShieldCheck },
              { key: 'privacy_content', label: 'سياسة الخصوصية', description: 'سياسة خصوصية واستخدام البيانات', icon: Lock },
            ].map((page, i) => (
              <motion.div key={page.key} variants={fadeUp} custom={i + 1}>
                <div className={`rounded-xl p-5 ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50/50 border border-gray-100'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <page.icon className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                    <div>
                      <h4 className={`text-sm font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{page.label}</h4>
                      <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{page.description}</p>
                    </div>
                  </div>
                  <Textarea value={pagesContent[page.key]} onChange={e => setPagesContent(p => ({ ...p, [page.key]: e.target.value }))} rows={8} placeholder={`أدخل محتوى ${page.label}... (يدعم HTML)`} className={`rounded-xl font-mono text-xs leading-relaxed ${inputCl}`} />
                  <div className="flex items-center justify-between mt-3">
                    <span className={`text-[10px] ${isDark ? 'text-white/20' : 'text-gray-400'}`}>{(pagesContent[page.key] || '').length} حرف</span>
                    <Badge className={`text-[10px] ${isDark ? 'bg-white/[0.04] text-white/30 border-white/[0.06]' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>HTML</Badge>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="flex justify-end mt-8">
            <SaveButton onClick={save} loading={loading} isDark={isDark} label="حفظ جميع الصفحات" />
          </div>
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}

// TAB: Facebook
function FacebookTab({ isDark, card, inputCl, textP, textS }) {
  const [fbConnectedPages, setFbConnectedPages] = useState([]);
  const [fbConnectedUsers, setFbConnectedUsers] = useState([]);
  const [fbVideos, setFbVideos] = useState([]);
  const [fbVideosTotal, setFbVideosTotal] = useState(0);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbConnectPage, setFbConnectPage] = useState({ page_id: '', page_access_token: '', page_name: '' });
  const [fbConnectUser, setFbConnectUser] = useState({ user_access_token: '' });
  const [fbPublish, setFbPublish] = useState({
    target_id: '', access_token: '', file_url: '', title: '', description: '',
    published: true, scheduled_publish_time: '', embeddable: true,
    content_category: '', sponsor_id: '', no_story: false, secret: false,
    spherical: false, privacy: '',
  });
  const [fbPublishLoading, setFbPublishLoading] = useState(false);

  const FB_CONTENT_CATEGORIES = [
    'BEAUTY_FASHION', 'BUSINESS', 'CARS_TRUCKS', 'COMEDY', 'CUTE_ANIMALS',
    'ENTERTAINMENT', 'FAMILY', 'FOOD_HEALTH', 'HOME', 'LIFESTYLE',
    'MUSIC', 'NEWS', 'POLITICS', 'SCIENCE', 'SPORTS', 'TECHNOLOGY',
    'VIDEO_GAMING', 'OTHER'
  ];

  const loadFacebookData = useCallback(async () => {
    try {
      const [accountsRes, videosRes] = await Promise.all([
        facebookAPI.getConnectedAccounts(),
        facebookAPI.listVideos({ page: 1, limit: 20 }),
      ]);
      setFbConnectedPages(accountsRes.data.pages || []);
      setFbConnectedUsers(accountsRes.data.users || []);
      setFbVideos(videosRes.data.videos || []);
      setFbVideosTotal(videosRes.data.total || 0);
    } catch {}
  }, []);

  useEffect(() => { loadFacebookData(); }, [loadFacebookData]);

  const handleConnectFBPage = async () => {
    if (!fbConnectPage.page_id || !fbConnectPage.page_access_token) { toast.error('معرف الصفحة ورمز الوصول مطلوبان'); return; }
    setFbLoading(true);
    try { await facebookAPI.connectPage(fbConnectPage); toast.success('تم ربط صفحة فيسبوك!'); setFbConnectPage({ page_id: '', page_access_token: '', page_name: '' }); loadFacebookData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل ربط الصفحة'); }
    finally { setFbLoading(false); }
  };

  const handleConnectFBUser = async () => {
    if (!fbConnectUser.user_access_token) { toast.error('رمز وصول المستخدم مطلوب'); return; }
    setFbLoading(true);
    try { await facebookAPI.connectUser(fbConnectUser); toast.success('تم ربط مستخدم فيسبوك!'); setFbConnectUser({ user_access_token: '' }); loadFacebookData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل ربط المستخدم'); }
    finally { setFbLoading(false); }
  };

  const handleDisconnectFBPage = async (pageId) => {
    try { await facebookAPI.disconnectPage(pageId); toast.success('تم إلغاء ربط الصفحة'); loadFacebookData(); }
    catch { toast.error('فشل إلغاء الربط'); }
  };

  const handleDisconnectFBUser = async (userId) => {
    try { await facebookAPI.disconnectUser(userId); toast.success('تم إلغاء ربط المستخدم'); loadFacebookData(); }
    catch { toast.error('فشل إلغاء الربط'); }
  };

  const handleFBPublishVideo = async () => {
    if (!fbPublish.target_id || !fbPublish.access_token || !fbPublish.file_url) { toast.error('معرف الهدف ورمز الوصول ورابط الملف مطلوبة'); return; }
    setFbPublishLoading(true);
    try {
      const payload = { ...fbPublish };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
      if (payload.scheduled_publish_time) payload.scheduled_publish_time = parseInt(payload.scheduled_publish_time);
      const res = await facebookAPI.publishVideo(payload);
      toast.success(`تم نشر الفيديو! ID: ${res.data.id || 'نجاح'}`);
      setFbPublish(p => ({ ...p, file_url: '', title: '', description: '' }));
      loadFacebookData();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل نشر الفيديو'); }
    finally { setFbPublishLoading(false); }
  };

  const handleDeleteFBVideo = async (videoId) => {
    try { await facebookAPI.deleteVideo(videoId, { access_token: fbPublish.access_token }); toast.success('تم حذف الفيديو'); loadFacebookData(); }
    catch { toast.error('فشل حذف الفيديو'); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      {/* Connected Accounts */}
      <motion.div variants={fadeUp}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Link2} title="حسابات فيسبوك المتصلة" subtitle="ربط صفحات وحسابات المستخدمين لنشر الفيديو" isDark={isDark} />

          {fbConnectedPages.length > 0 && (
            <div className="mb-6">
              <h4 className={`text-sm font-semibold mb-3 ${textP}`}>الصفحات المتصلة</h4>
              <div className="space-y-2">
                {fbConnectedPages.map(p => (
                  <motion.div key={p.page_id} whileHover={{ x: 4 }} className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50 border border-gray-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                        <Play className="w-4 h-4 text-blue-500" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${textP}`}>{p.page_name || p.page_id}</p>
                        <p className={`text-xs ${textS}`}>ID: {p.page_id}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDisconnectFBPage(p.page_id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                      <Unlink className="w-4 h-4 ml-1" /> إلغاء الربط
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {fbConnectedUsers.length > 0 && (
            <div className="mb-6">
              <h4 className={`text-sm font-semibold mb-3 ${textP}`}>المستخدمون المتصلون</h4>
              <div className="space-y-2">
                {fbConnectedUsers.map(u => (
                  <motion.div key={u.user_id} whileHover={{ x: 4 }} className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50 border border-gray-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                        <Users className="w-4 h-4 text-emerald-500" />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${textP}`}>{u.user_name || u.user_id}</p>
                        <p className={`text-xs ${textS}`}>ID: {u.user_id}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDisconnectFBUser(u.user_id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                      <Unlink className="w-4 h-4 ml-1" /> إلغاء الربط
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Connect Page Form */}
          <div className={`rounded-xl p-5 mb-4 ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50/50 border border-gray-100'}`}>
            <h4 className={`text-sm font-semibold mb-3 ${textP}`}>ربط صفحة فيسبوك</h4>
            <p className={`text-xs mb-4 ${textS}`}>يتطلب: pages_manage_posts, pages_read_engagement, pages_show_list</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldGroup label="معرف الصفحة" isDark={isDark}>
                <Input value={fbConnectPage.page_id} onChange={e => setFbConnectPage(p => ({ ...p, page_id: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="123456789..." />
              </FieldGroup>
              <FieldGroup label="رمز وصول الصفحة" isDark={isDark}>
                <Input value={fbConnectPage.page_access_token} onChange={e => setFbConnectPage(p => ({ ...p, page_access_token: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="EAAGm..." type="password" />
              </FieldGroup>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={handleConnectFBPage} disabled={fbLoading} className={`rounded-xl px-5 h-9 text-sm ${isDark ? 'bg-blue-500 hover:bg-blue-400 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                {fbLoading ? <RefreshCw className="w-4 h-4 animate-spin ml-2" /> : <Link className="w-4 h-4 ml-2" />}
                ربط الصفحة
              </Button>
            </div>
          </div>

          {/* Connect User Form */}
          <div className={`rounded-xl p-5 ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50/50 border border-gray-100'}`}>
            <h4 className={`text-sm font-semibold mb-3 ${textP}`}>ربط مستخدم فيسبوك</h4>
            <p className={`text-xs mb-4 ${textS}`}>يتطلب: user_videos, publish_video</p>
            <FieldGroup label="رمز وصول المستخدم" isDark={isDark}>
              <Input value={fbConnectUser.user_access_token} onChange={e => setFbConnectUser(p => ({ ...p, user_access_token: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="EAAGm..." type="password" />
            </FieldGroup>
            <div className="flex justify-end mt-4">
              <Button onClick={handleConnectFBUser} disabled={fbLoading} className={`rounded-xl px-5 h-9 text-sm ${isDark ? 'bg-emerald-500 hover:bg-emerald-400 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}>
                {fbLoading ? <RefreshCw className="w-4 h-4 animate-spin ml-2" /> : <Link className="w-4 h-4 ml-2" />}
                ربط المستخدم
              </Button>
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Publish Video */}
      <motion.div variants={fadeUp} custom={1}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Upload} title="نشر فيديو" subtitle="رفع ونشر الفيديوهات على فيسبوك عبر Graph API v25.0" isDark={isDark} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FieldGroup label="معرف الهدف (صفحة/مستخدم)" description="معرف الصفحة أو المستخدم للنشر عليه" isDark={isDark}>
              <Input value={fbPublish.target_id} onChange={e => setFbPublish(p => ({ ...p, target_id: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="معرف الصفحة أو المستخدم" />
            </FieldGroup>
            <FieldGroup label="رمز الوصول" description="رمز وصول الصفحة أو المستخدم" isDark={isDark}>
              <Input value={fbPublish.access_token} onChange={e => setFbPublish(p => ({ ...p, access_token: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="EAAGm..." type="password" />
            </FieldGroup>
          </div>

          <FieldGroup label="رابط ملف الفيديو" description="رابط مباشر للملف" isDark={isDark}>
            <Input value={fbPublish.file_url} onChange={e => setFbPublish(p => ({ ...p, file_url: e.target.value }))} className={`${inputCl} rounded-lg h-9 mb-4`} placeholder="https://example.com/video.mp4" />
          </FieldGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FieldGroup label="العنوان" isDark={isDark}>
              <Input value={fbPublish.title} onChange={e => setFbPublish(p => ({ ...p, title: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="عنوان الفيديو" />
            </FieldGroup>
            <FieldGroup label="تصنيف المحتوى" isDark={isDark}>
              <select value={fbPublish.content_category} onChange={e => setFbPublish(p => ({ ...p, content_category: e.target.value }))} className={`w-full rounded-lg h-9 px-3 text-sm ${inputCl} border`}>
                <option value="">اختر التصنيف...</option>
                {FB_CONTENT_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </FieldGroup>
          </div>

          <FieldGroup label="الوصف" isDark={isDark}>
            <Textarea value={fbPublish.description} onChange={e => setFbPublish(p => ({ ...p, description: e.target.value }))} rows={3} className={`${inputCl} rounded-lg mb-4`} placeholder="وصف الفيديو..." />
          </FieldGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FieldGroup label="معرف الراعي" description="معرف صفحة فيسبوك لوسم كراعي" isDark={isDark}>
              <Input value={fbPublish.sponsor_id} onChange={e => setFbPublish(p => ({ ...p, sponsor_id: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="معرف الراعي (اختياري)" />
            </FieldGroup>
            <FieldGroup label="وقت النشر المجدول" description="طابع زمني Unix (10 دقائق - 6 أشهر)" isDark={isDark}>
              <Input value={fbPublish.scheduled_publish_time} onChange={e => setFbPublish(p => ({ ...p, scheduled_publish_time: e.target.value }))} className={`${inputCl} rounded-lg h-9`} placeholder="طابع زمني Unix (اختياري)" type="number" />
            </FieldGroup>
          </div>

          <div className={`grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 p-4 rounded-xl ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50/50 border border-gray-100'}`}>
            {[
              { key: 'published', label: 'منشور' },
              { key: 'embeddable', label: 'قابل للتضمين' },
              { key: 'no_story', label: 'بدون قصة' },
              { key: 'secret', label: 'سري' },
              { key: 'spherical', label: 'فيديو 360°' },
            ].map(opt => (
              <div key={opt.key} className="flex items-center justify-between gap-2">
                <span className={`text-xs ${textS}`}>{opt.label}</span>
                <ToggleSwitch enabled={fbPublish[opt.key]} onToggle={() => setFbPublish(p => ({ ...p, [opt.key]: !p[opt.key] }))} isDark={isDark} />
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button onClick={handleFBPublishVideo} disabled={fbPublishLoading} className={`rounded-xl px-6 h-10 font-medium ${isDark ? 'bg-blue-500 hover:bg-blue-400 text-white shadow-lg shadow-blue-500/20' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'}`}>
                {fbPublishLoading ? <RefreshCw className="w-4 h-4 animate-spin ml-2" /> : <Upload className="w-4 h-4 ml-2" />}
                نشر الفيديو
              </Button>
            </motion.div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Published Videos */}
      <motion.div variants={fadeUp} custom={2}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Video} title="الفيديوهات المنشورة" subtitle={`${fbVideosTotal} فيديو منشور عبر فيسبوك API`} isDark={isDark} />
          {fbVideos.length === 0 ? (
            <EmptyState icon={Video} title="لم يتم نشر أي فيديو بعد" isDark={isDark} />
          ) : (
            <div className="space-y-3">
              {fbVideos.map((v, i) => (
                <motion.div key={v.id || i} variants={fadeUp} custom={i} whileHover={{ x: 4 }}
                  className={`flex items-center justify-between p-4 rounded-xl ${isDark ? 'bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04]' : 'bg-gray-50 border border-gray-100 hover:bg-gray-100/80'} transition-colors`}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                      <Play className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${textP}`}>{v.title || 'فيديو بدون عنوان'}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs ${textS}`}>ID: {v.fb_video_id || '-'}</span>
                        <Badge className={`text-[10px] ${v.status === 'published' ? (isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200') : (isDark ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-yellow-50 text-yellow-600 border-yellow-200')}`}>
                          {v.status || 'غير معروف'}
                        </Badge>
                        <span className={`text-xs ${textS}`}>{v.created_at ? new Date(v.created_at).toLocaleDateString('ar') : ''}</span>
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteFBVideo(v.fb_video_id || v.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0 mr-2">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}

// TAB: Users Management
function UsersTab({ isDark, card, inputCl, textP, textS }) {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'subscriber', display_name: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = { page: currentPage, limit: 20 };
      if (searchQuery) params.search = searchQuery;
      if (roleFilter !== 'all') params.role = roleFilter;
      const res = await adminAPI.users(params);
      setUsers(Array.isArray(res.data) ? res.data : res.data.users || res.data.items || []);
      setTotalUsers(res.data.total || (Array.isArray(res.data) ? res.data.length : 0));
    } catch { toast.error('فشل تحميل المستخدمين'); }
    finally { setUsersLoading(false); }
  }, [currentPage, searchQuery, roleFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) { toast.error('البريد الإلكتروني وكلمة المرور مطلوبان'); return; }
    setCreateLoading(true);
    try {
      await adminAPI.createUser(newUser);
      toast.success('تم إنشاء المستخدم بنجاح');
      setNewUser({ username: '', email: '', password: '', role: 'subscriber', display_name: '' });
      setShowCreateModal(false);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل إنشاء المستخدم'); }
    finally { setCreateLoading(false); }
  };

  const handleChangeRole = async (userId, newRole) => {
    try { await adminAPI.updateRole(userId, newRole); toast.success('تم تحديث الدور'); loadUsers(); }
    catch { toast.error('فشل تحديث الدور'); }
  };

  const handleBanUser = async (userId) => {
    try { await adminAPI.blockUser(userId); toast.success('تم تحديث حالة الحظر'); loadUsers(); }
    catch { toast.error('فشل تحديث الحالة'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    try { await adminAPI.deleteUser(userId); toast.success('تم حذف المستخدم'); loadUsers(); }
    catch { toast.error('فشل حذف المستخدم'); }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) { toast.error('اختر مستخدمين أولاً'); return; }
    for (const userId of selectedUsers) {
      try {
        if (action === 'ban') await adminAPI.blockUser(userId);
        else if (action === 'delete') await adminAPI.deleteUser(userId);
      } catch {}
    }
    toast.success(`تم تنفيذ الإجراء على ${selectedUsers.length} مستخدم`);
    setSelectedUsers([]);
    loadUsers();
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) setSelectedUsers([]);
    else setSelectedUsers(users.map(u => u.id || u._id));
  };

  const filteredUsers = users;

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      {/* Header with stats */}
      <motion.div variants={fadeUp}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'إجمالي المستخدمين', value: totalUsers, icon: Users, color: 'blue' },
            { label: 'المدراء', value: users.filter(u => u.role === 'admin').length, icon: ShieldCheck, color: 'red' },
            { label: 'الجهات المختصة', value: users.filter(u => u.role === 'authority').length, icon: Building2, color: 'blue' },
            { label: 'المسوقون', value: users.filter(u => u.role === 'affiliate').length, icon: Zap, color: 'emerald' },
          ].map((stat, i) => (
            <motion.div key={stat.label} variants={fadeUp} custom={i} className={`rounded-2xl p-4 backdrop-blur-xl ${isDark ? 'bg-[#0A0A0C]/60 border border-white/[0.05]' : 'bg-white/80 border border-gray-200/80 shadow-sm'}`}>
              <stat.icon className={`w-5 h-5 text-${stat.color}-500 mb-2`} />
              <p className={`text-2xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
              <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Search, Filter, Actions */}
      <motion.div variants={fadeUp} custom={1}>
        <GlassCard isDark={isDark} hover={false}>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/20' : 'text-gray-400'}`} />
              <Input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }} placeholder="بحث بالاسم أو البريد الإلكتروني..." className={`rounded-xl h-10 pr-10 ${inputCl}`} />
            </div>
            <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setCurrentPage(1); }} className={`h-10 rounded-xl border px-3 text-sm w-full sm:w-40 ${inputCl}`}>
              <option value="all" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>جميع الأدوار</option>
              <option value="admin" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>مدير</option>
              <option value="authority" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>جهة مختصة</option>
              <option value="affiliate" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>مسوق</option>
              <option value="subscriber" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>مشترك</option>
            </select>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button onClick={() => setShowCreateModal(true)} className={`rounded-xl h-10 px-5 ${isDark ? 'bg-blue-500 hover:bg-blue-400 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                <UserPlus className="w-4 h-4 ml-2" /> إضافة مستخدم
              </Button>
            </motion.div>
          </div>

          {/* Bulk Actions */}
          <AnimatePresence>
            {selectedUsers.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className={`flex items-center gap-3 mb-4 p-3 rounded-xl ${isDark ? 'bg-blue-500/5 border border-blue-500/10' : 'bg-blue-50 border border-blue-200'}`}>
                <span className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{selectedUsers.length} مختار</span>
                <Button size="sm" variant="ghost" onClick={() => handleBulkAction('ban')} className="text-amber-500 hover:bg-amber-500/10">
                  <Ban className="w-3.5 h-3.5 ml-1" /> حظر
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleBulkAction('delete')} className="text-red-500 hover:bg-red-500/10">
                  <Trash2 className="w-3.5 h-3.5 ml-1" /> حذف
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedUsers([])} className={isDark ? 'text-white/40' : 'text-gray-400'}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Users Table */}
          {usersLoading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className={`w-6 h-6 animate-spin ${isDark ? 'text-white/20' : 'text-gray-400'}`} />
            </div>
          ) : filteredUsers.length === 0 ? (
            <EmptyState icon={Users} title="لا يوجد مستخدمين" subtitle="جرب تغيير معايير البحث" isDark={isDark} />
          ) : (
            <div className="space-y-2">
              {/* Header Row */}
              <div className={`grid grid-cols-12 gap-3 px-4 py-2 text-xs font-medium ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                <div className="col-span-1">
                  <input type="checkbox" checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0} onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
                </div>
                <div className="col-span-3">المستخدم</div>
                <div className="col-span-2">البريد</div>
                <div className="col-span-1">الدور</div>
                <div className="col-span-2">التسجيل</div>
                <div className="col-span-1">الحالة</div>
                <div className="col-span-2 text-left">إجراءات</div>
              </div>

              {/* User Rows */}
              {filteredUsers.map((user, i) => (
                <motion.div key={user.id || user._id || i} variants={slideIn} custom={i}
                  className={`grid grid-cols-12 gap-3 items-center px-4 py-3 rounded-xl transition-all ${
                    selectedUsers.includes(user.id || user._id)
                      ? isDark ? 'bg-blue-500/5 border border-blue-500/10' : 'bg-blue-50 border border-blue-200'
                      : isDark ? 'bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04]' : 'bg-gray-50 border border-gray-100 hover:bg-gray-100/80'
                  }`}>
                  <div className="col-span-1">
                    <input type="checkbox" checked={selectedUsers.includes(user.id || user._id)} onChange={() => toggleSelectUser(user.id || user._id)}
                      className="rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
                  </div>
                  <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      isDark ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-white/60' : 'bg-gradient-to-br from-blue-100 to-purple-100 text-blue-600'
                    }`}>
                      {(user.display_name || user.username || user.email || '?')[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${textP}`}>{user.display_name || user.username || '-'}</p>
                      <p className={`text-[10px] truncate ${isDark ? 'text-white/20' : 'text-gray-400'}`}>@{user.username || '-'}</p>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-600'}`}>{user.email}</p>
                  </div>
                  <div className="col-span-1">
                    <RoleBadge role={user.role} isDark={isDark} />
                  </div>
                  <div className="col-span-2">
                    <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{user.created_at ? new Date(user.created_at).toLocaleDateString('ar') : '-'}</p>
                  </div>
                  <div className="col-span-1">
                    <StatusDot active={!user.is_blocked && user.is_active !== false} isDark={isDark} />
                  </div>
                  <div className="col-span-2 flex items-center gap-1 justify-end">
                    <select value={user.role} onChange={e => handleChangeRole(user.id || user._id, e.target.value)}
                      className={`h-7 rounded-lg border px-1.5 text-[10px] ${inputCl}`}>
                      <option value="admin">مدير</option>
                      <option value="authority">جهة مختصة</option>
                      <option value="affiliate">مسوق</option>
                      <option value="subscriber">مشترك</option>
                    </select>
                    <Button variant="ghost" size="icon" onClick={() => handleBanUser(user.id || user._id)}
                      className={`h-7 w-7 rounded-lg ${isDark ? 'hover:bg-amber-500/10 text-white/30 hover:text-amber-400' : 'hover:bg-amber-50 text-gray-400 hover:text-amber-600'}`}>
                      <Ban className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(user.id || user._id)}
                      className={`h-7 w-7 rounded-lg ${isDark ? 'hover:bg-red-500/10 text-white/30 hover:text-red-400' : 'hover:bg-red-50 text-gray-400 hover:text-red-600'}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalUsers > 20 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className={`rounded-lg ${isDark ? 'text-white/40' : 'text-gray-500'}`}>السابق</Button>
              <span className={`text-sm px-3 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{currentPage} / {Math.ceil(totalUsers / 20)}</span>
              <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= Math.ceil(totalUsers / 20)}
                className={`rounded-lg ${isDark ? 'text-white/40' : 'text-gray-500'}`}>التالي</Button>
            </div>
          )}
        </GlassCard>
      </motion.div>

      {/* Create User Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`w-full max-w-lg rounded-2xl p-6 ${isDark ? 'bg-[#0A0A0C] border border-white/[0.08]' : 'bg-white border border-gray-200 shadow-2xl'}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                    <UserPlus className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>إضافة مستخدم جديد</h3>
                    <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>إنشاء حساب مستخدم جديد</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)} className={`rounded-lg ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FieldGroup label="اسم المستخدم" isDark={isDark}>
                    <Input value={newUser.username} onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} placeholder="username" className={`rounded-xl h-10 ${inputCl}`} />
                  </FieldGroup>
                  <FieldGroup label="الاسم المعروض" isDark={isDark}>
                    <Input value={newUser.display_name} onChange={e => setNewUser(p => ({ ...p, display_name: e.target.value }))} placeholder="الاسم الكامل" className={`rounded-xl h-10 ${inputCl}`} />
                  </FieldGroup>
                </div>
                <FieldGroup label="البريد الإلكتروني" isDark={isDark}>
                  <Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="user@example.com" className={`rounded-xl h-10 ${inputCl}`} />
                </FieldGroup>
                <FieldGroup label="كلمة المرور" isDark={isDark}>
                  <Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="كلمة مرور قوية" className={`rounded-xl h-10 ${inputCl}`} />
                </FieldGroup>
                <FieldGroup label="الدور" isDark={isDark}>
                  <select value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} className={`w-full h-10 rounded-xl border px-3 text-sm ${inputCl}`}>
                    <option value="subscriber">مشترك</option>
                    <option value="affiliate">مسوق</option>
                    <option value="authority">جهة مختصة</option>
                    <option value="admin">مدير</option>
                  </select>
                </FieldGroup>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreateModal(false)} className={`rounded-xl ${isDark ? 'text-white/40' : 'text-gray-500'}`}>إلغاء</Button>
                <SaveButton onClick={handleCreateUser} loading={createLoading} isDark={isDark} label="إنشاء المستخدم" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// TAB: Authorities Management
function AuthoritiesTab({ isDark, card, inputCl, textP, textS }) {
  const [authorities, setAuthorities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await adminAPI.users({ role: 'authority', limit: 100 });
        setAuthorities(Array.isArray(res.data) ? res.data : res.data.users || res.data.items || []);
      } catch { toast.error('فشل تحميل الجهات المختصة'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      {/* Stats */}
      <motion.div variants={fadeUp}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: 'إجمالي الجهات المختصة', value: authorities.length, icon: Building2, color: 'blue' },
            { label: 'نشط الآن', value: authorities.filter(a => !a.is_blocked).length, icon: UserCheck, color: 'emerald' },
            { label: 'محظور', value: authorities.filter(a => a.is_blocked).length, icon: UserX, color: 'red' },
          ].map((stat, i) => (
            <motion.div key={stat.label} variants={fadeUp} custom={i} className={`rounded-2xl p-5 backdrop-blur-xl ${isDark ? 'bg-[#0A0A0C]/60 border border-white/[0.05]' : 'bg-white/80 border border-gray-200/80 shadow-sm'}`}>
              <div className="flex items-center justify-between mb-3">
                <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
                <Badge className={`text-[10px] ${isDark ? 'bg-white/[0.04] text-white/40 border-white/[0.06]' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>{stat.label}</Badge>
              </div>
              <p className={`text-3xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div variants={fadeUp} custom={1}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Building2} title="الجهات المختصة" subtitle="إدارة المستخدمين ذوي صلاحية الجهات المختصة" isDark={isDark} />

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className={`w-6 h-6 animate-spin ${isDark ? 'text-white/20' : 'text-gray-400'}`} />
            </div>
          ) : authorities.length === 0 ? (
            <EmptyState icon={Building2} title="لا يوجد جهات مختصة" subtitle="قم بتعيين دور جهة مختصة لمستخدم من تبويب المستخدمين" isDark={isDark} />
          ) : (
            <div className="space-y-3">
              {authorities.map((auth, i) => (
                <motion.div key={auth.id || auth._id || i} variants={slideIn} custom={i}
                  whileHover={{ x: 4 }}
                  className={`flex items-center justify-between p-4 rounded-xl transition-all ${isDark ? 'bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04]' : 'bg-gray-50 border border-gray-100 hover:bg-gray-100/80'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold ${isDark ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20 text-blue-400' : 'bg-gradient-to-br from-blue-100 to-cyan-100 text-blue-600'}`}>
                      {(auth.display_name || auth.username || auth.email || '?')[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${textP}`}>{auth.display_name || auth.username || '-'}</p>
                      <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{auth.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusDot active={!auth.is_blocked} isDark={isDark} />
                        <span className={`text-[10px] ${isDark ? 'text-white/20' : 'text-gray-400'}`}>
                          {auth.is_blocked ? 'محظور' : 'نشط'}
                        </span>
                        {auth.created_at && (
                          <span className={`text-[10px] ${isDark ? 'text-white/15' : 'text-gray-300'}`}>
                            منذ {new Date(auth.created_at).toLocaleDateString('ar')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <RoleBadge role="authority" isDark={isDark} />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}

// TAB: Activity Log
function ActivityTab({ isDark, card, inputCl, textP, textS }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: 30 };
      if (actionFilter !== 'all') params.action = actionFilter;
      const res = await adminAPI.activityLog(params);
      setLogs(Array.isArray(res.data) ? res.data : res.data.items || res.data.logs || []);
    } catch { toast.error('فشل تحميل سجل النشاطات'); }
    finally { setLoading(false); }
  }, [currentPage, actionFilter]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const actionColors = {
    login: 'blue', logout: 'gray', create: 'emerald', update: 'amber', delete: 'red', block: 'red', role_change: 'purple',
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      <motion.div variants={fadeUp}>
        <GlassCard isDark={isDark}>
          <SectionTitle icon={Activity} title="سجل النشاطات" subtitle="تتبع جميع الإجراءات والأحداث في المنصة" isDark={isDark} />

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setCurrentPage(1); }}
              className={`h-10 rounded-xl border px-3 text-sm w-full sm:w-48 ${inputCl}`}>
              <option value="all" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>جميع الإجراءات</option>
              <option value="login" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>تسجيل دخول</option>
              <option value="create" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>إنشاء</option>
              <option value="update" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>تحديث</option>
              <option value="delete" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>حذف</option>
              <option value="block" className={isDark ? 'bg-[#0A0A0C]' : 'bg-white'}>حظر</option>
            </select>
            <Button variant="ghost" onClick={loadLogs} className={`rounded-xl h-10 px-4 ${isDark ? 'text-white/40 hover:text-white/60' : 'text-gray-500 hover:text-gray-700'}`}>
              <RefreshCw className="w-4 h-4 ml-2" /> تحديث
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className={`w-6 h-6 animate-spin ${isDark ? 'text-white/20' : 'text-gray-400'}`} />
            </div>
          ) : logs.length === 0 ? (
            <EmptyState icon={Activity} title="لا يوجد نشاطات" subtitle="ستظهر هنا جميع الإجراءات والأحداث" isDark={isDark} />
          ) : (
            <div className="space-y-2">
              {/* Header */}
              <div className={`grid grid-cols-12 gap-3 px-4 py-2 text-xs font-medium ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                <div className="col-span-2">المستخدم</div>
                <div className="col-span-2">الإجراء</div>
                <div className="col-span-4">التفاصيل</div>
                <div className="col-span-2">IP</div>
                <div className="col-span-2">الوقت</div>
              </div>

              {logs.map((log, i) => {
                const color = actionColors[log.action] || 'gray';
                return (
                  <motion.div key={log.id || i} variants={slideIn} custom={i}
                    className={`grid grid-cols-12 gap-3 items-center px-4 py-3 rounded-xl ${isDark ? 'bg-white/[0.02] border border-white/[0.04]' : 'bg-gray-50 border border-gray-100'}`}>
                    <div className="col-span-2">
                      <p className={`text-xs font-medium truncate ${textP}`}>{log.user_name || log.user_email || log.user_id || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <Badge className={`text-[10px] ${isDark ? `bg-${color}-500/10 text-${color}-400 border-${color}-500/20` : `bg-${color}-50 text-${color}-600 border-${color}-200`}`}>
                        {log.action || '-'}
                      </Badge>
                    </div>
                    <div className="col-span-4">
                      <p className={`text-xs truncate ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{log.details || log.description || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className={`text-xs font-mono ${isDark ? 'text-white/20' : 'text-gray-400'}`}>{log.ip || log.ip_address || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className={`text-xs ${isDark ? 'text-white/20' : 'text-gray-400'}`}>{log.created_at ? new Date(log.created_at).toLocaleString('ar') : '-'}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className={`rounded-lg ${isDark ? 'text-white/40' : 'text-gray-500'}`}>السابق</Button>
            <span className={`text-sm px-3 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>صفحة {currentPage}</span>
            <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => p + 1)} disabled={logs.length < 30}
              className={`rounded-lg ${isDark ? 'text-white/40' : 'text-gray-500'}`}>التالي</Button>
          </div>
        </GlassCard>
      </motion.div>
    </motion.div>
  );
}

// TAB: Advanced Payment Gateways
function PaymentAdvancedTab({ isDark, card, inputCl, textP, textS }) {
  const [paypalConfig, setPaypalConfig] = useState({ client_id: '', secret_key: '', sandbox: true, status: 'disconnected' });
  const [stripeConfig, setStripeConfig] = useState({ publishable_key: '', secret_key: '', webhook_secret: '', test_mode: true, status: 'disconnected' });
  const [bankConfig, setBankConfig] = useState({ bank_name: '', iban: '', swift: '', account_holder: '', instructions: '' });
  const [localConfig, setLocalConfig] = useState({ provider_name: '', account_number: '', instructions: '' });
  const [enabledGateways, setEnabledGateways] = useState({ paypal: false, stripe: false, bank: false, local: false });
  const [loading, setLoading] = useState({});
  const [testing, setTesting] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const ids = ['stripe', 'paypal', 'bank_transfer', 'western_union'];
        const results = await Promise.allSettled(ids.map(id => api.get(`/admin/payment-gateways/${id}`)));
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value.data) {
            const d = r.value.data;
            if (ids[i] === 'paypal') { setPaypalConfig(p => ({ ...p, ...d })); setEnabledGateways(p => ({ ...p, paypal: d.enabled || false })); }
            if (ids[i] === 'stripe') { setStripeConfig(p => ({ ...p, ...d })); setEnabledGateways(p => ({ ...p, stripe: d.enabled || false })); }
            if (ids[i] === 'bank_transfer') { setBankConfig(p => ({ ...p, ...d })); setEnabledGateways(p => ({ ...p, bank: d.enabled || false })); }
            if (ids[i] === 'western_union') { setLocalConfig(p => ({ ...p, ...d })); setEnabledGateways(p => ({ ...p, local: d.enabled || false })); }
          }
        });
      } catch {}
    })();
  }, []);

  const savePaymentGateway = async (id, data) => {
    setLoading(p => ({ ...p, [id]: true }));
    try { await api.put(`/admin/payment-gateways/${id}`, { ...data, enabled: enabledGateways[id === 'bank_transfer' ? 'bank' : id === 'western_union' ? 'local' : id] }); toast.success('تم حفظ إعدادات البوابة'); }
    catch { toast.error('فشل حفظ الإعدادات'); }
    finally { setLoading(p => ({ ...p, [id]: false })); }
  };

  const testConnection = async (gateway) => {
    setTesting(p => ({ ...p, [gateway]: true }));
    try {
      await new Promise(r => setTimeout(r, 1500));
      toast.success(`اتصال ${gateway} ناجح!`);
    } catch { toast.error('فشل اختبار الاتصال'); }
    finally { setTesting(p => ({ ...p, [gateway]: false })); }
  };

  const gatewayCards = [
    {
      id: 'paypal', key: 'paypal', label: 'PayPal', icon: DollarSign, color: 'blue',
      description: 'قبول المدفوعات عبر PayPal Checkout',
      content: (
        <div className="space-y-4">
          <FieldGroup label="معرف العميل (Client ID)" isDark={isDark}>
            <Input value={paypalConfig.client_id} onChange={e => setPaypalConfig(p => ({ ...p, client_id: e.target.value }))} placeholder="AX..." className={`rounded-xl h-10 ${inputCl}`} />
          </FieldGroup>
          <FieldGroup label="المفتاح السري (Secret Key)" isDark={isDark}>
            <Input type="password" value={paypalConfig.client_secret || paypalConfig.secret_key} onChange={e => setPaypalConfig(p => ({ ...p, client_secret: e.target.value }))} placeholder="EL..." className={`rounded-xl h-10 ${inputCl}`} />
          </FieldGroup>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>وضع الاختبار (Sandbox)</span>
            <ToggleSwitch enabled={paypalConfig.mode === 'sandbox' || paypalConfig.sandbox} onToggle={() => setPaypalConfig(p => ({ ...p, mode: p.mode === 'sandbox' ? 'live' : 'sandbox', sandbox: !p.sandbox }))} isDark={isDark} />
          </div>
          <div className="flex gap-3 pt-2">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button onClick={() => testConnection('paypal')} disabled={testing.paypal}
                className={`rounded-xl h-9 px-4 text-sm ${isDark ? 'bg-white/[0.04] hover:bg-white/[0.08] text-white/60 border border-white/[0.06]' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'}`}>
                {testing.paypal ? <RefreshCw className="w-3.5 h-3.5 animate-spin ml-2" /> : <TestTube className="w-3.5 h-3.5 ml-2" />}
                اختبار الاتصال
              </Button>
            </motion.div>
            <SaveButton onClick={() => savePaymentGateway('paypal', paypalConfig)} loading={loading.paypal} isDark={isDark} label="حفظ PayPal" />
          </div>
        </div>
      ),
    },
    {
      id: 'stripe', key: 'stripe', label: 'بطاقة ائتمان (Stripe)', icon: CreditCard, color: 'purple',
      description: 'قبول بطاقات الائتمان والخصم عبر Stripe',
      content: (
        <div className="space-y-4">
          <FieldGroup label="المفتاح العام (Publishable Key)" isDark={isDark}>
            <Input value={stripeConfig.publishable_key} onChange={e => setStripeConfig(p => ({ ...p, publishable_key: e.target.value }))} placeholder="pk_live_..." className={`rounded-xl h-10 ${inputCl}`} />
          </FieldGroup>
          <FieldGroup label="المفتاح السري (Secret Key)" isDark={isDark}>
            <Input type="password" value={stripeConfig.api_key || stripeConfig.secret_key} onChange={e => setStripeConfig(p => ({ ...p, api_key: e.target.value }))} placeholder="sk_live_..." className={`rounded-xl h-10 ${inputCl}`} />
          </FieldGroup>
          <FieldGroup label="مفتاح Webhook السري" isDark={isDark}>
            <Input type="password" value={stripeConfig.webhook_secret} onChange={e => setStripeConfig(p => ({ ...p, webhook_secret: e.target.value }))} placeholder="whsec_..." className={`rounded-xl h-10 ${inputCl}`} />
          </FieldGroup>
          <div className="flex items-center justify-between">
            <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>وضع الاختبار (Test Mode)</span>
            <ToggleSwitch enabled={stripeConfig.test_mode} onToggle={() => setStripeConfig(p => ({ ...p, test_mode: !p.test_mode }))} isDark={isDark} />
          </div>
          <div className="flex gap-3 pt-2">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button onClick={() => testConnection('stripe')} disabled={testing.stripe}
                className={`rounded-xl h-9 px-4 text-sm ${isDark ? 'bg-white/[0.04] hover:bg-white/[0.08] text-white/60 border border-white/[0.06]' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'}`}>
                {testing.stripe ? <RefreshCw className="w-3.5 h-3.5 animate-spin ml-2" /> : <TestTube className="w-3.5 h-3.5 ml-2" />}
                اختبار الاتصال
              </Button>
            </motion.div>
            <SaveButton onClick={() => savePaymentGateway('stripe', stripeConfig)} loading={loading.stripe} isDark={isDark} label="حفظ Stripe" />
          </div>
        </div>
      ),
    },
    {
      id: 'bank_transfer', key: 'bank', label: 'تحويل بنكي', icon: Landmark, color: 'emerald',
      description: 'استقبال المدفوعات عبر التحويل البنكي المباشر',
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldGroup label="اسم البنك" isDark={isDark}>
              <Input value={bankConfig.bank_name} onChange={e => setBankConfig(p => ({ ...p, bank_name: e.target.value }))} placeholder="البنك الأهلي" className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
            <FieldGroup label="صاحب الحساب" isDark={isDark}>
              <Input value={bankConfig.account_holder} onChange={e => setBankConfig(p => ({ ...p, account_holder: e.target.value }))} placeholder="اسم الشركة" className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldGroup label="رقم IBAN" isDark={isDark}>
              <Input value={bankConfig.iban} onChange={e => setBankConfig(p => ({ ...p, iban: e.target.value }))} placeholder="SA..." className={`rounded-xl h-10 font-mono ${inputCl}`} />
            </FieldGroup>
            <FieldGroup label="رمز SWIFT/BIC" isDark={isDark}>
              <Input value={bankConfig.swift} onChange={e => setBankConfig(p => ({ ...p, swift: e.target.value }))} placeholder="NCBKSAJE" className={`rounded-xl h-10 font-mono ${inputCl}`} />
            </FieldGroup>
          </div>
          <FieldGroup label="تعليمات التحويل" isDark={isDark}>
            <Textarea value={bankConfig.instructions} onChange={e => setBankConfig(p => ({ ...p, instructions: e.target.value }))} placeholder="تعليمات إضافية للتحويل البنكي..." rows={3} className={`rounded-xl ${inputCl}`} />
          </FieldGroup>
          <div className="flex justify-end pt-2">
            <SaveButton onClick={() => savePaymentGateway('bank_transfer', bankConfig)} loading={loading.bank_transfer} isDark={isDark} label="حفظ التحويل البنكي" />
          </div>
        </div>
      ),
    },
    {
      id: 'western_union', key: 'local', label: 'تحويل محلي', icon: Smartphone, color: 'amber',
      description: 'استقبال المدفوعات عبر خدمات التحويل المحلية',
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldGroup label="اسم مزود الخدمة" isDark={isDark}>
              <Input value={localConfig.provider_name || localConfig.receiver_name} onChange={e => setLocalConfig(p => ({ ...p, provider_name: e.target.value, receiver_name: e.target.value }))} placeholder="STC Pay / مدى" className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
            <FieldGroup label="رقم الحساب" isDark={isDark}>
              <Input value={localConfig.account_number || localConfig.country} onChange={e => setLocalConfig(p => ({ ...p, account_number: e.target.value, country: e.target.value }))} placeholder="05xxxxxxxx" className={`rounded-xl h-10 ${inputCl}`} />
            </FieldGroup>
          </div>
          <FieldGroup label="تعليمات الدفع" isDark={isDark}>
            <Textarea value={localConfig.instructions} onChange={e => setLocalConfig(p => ({ ...p, instructions: e.target.value }))} placeholder="تعليمات إضافية للدفع المحلي..." rows={3} className={`rounded-xl ${inputCl}`} />
          </FieldGroup>
          <div className="flex justify-end pt-2">
            <SaveButton onClick={() => savePaymentGateway('western_union', localConfig)} loading={loading.western_union} isDark={isDark} label="حفظ التحويل المحلي" />
          </div>
        </div>
      ),
    },
  ];

  const colorMap = {
    blue: { bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50', border: isDark ? 'border-blue-500/20' : 'border-blue-200', text: 'text-blue-500', gradient: 'from-blue-500/20 to-cyan-500/20' },
    purple: { bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50', border: isDark ? 'border-purple-500/20' : 'border-purple-200', text: 'text-purple-500', gradient: 'from-purple-500/20 to-pink-500/20' },
    emerald: { bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', border: isDark ? 'border-emerald-500/20' : 'border-emerald-200', text: 'text-emerald-500', gradient: 'from-emerald-500/20 to-teal-500/20' },
    amber: { bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50', border: isDark ? 'border-amber-500/20' : 'border-amber-200', text: 'text-amber-500', gradient: 'from-amber-500/20 to-orange-500/20' },
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      <motion.div variants={fadeUp}>
        <SectionTitle icon={CreditCard} title="بوابات الدفع المتقدمة" subtitle="تكوين مفصل لجميع طرق الدفع المتاحة" isDark={isDark} />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {gatewayCards.map((gw, i) => {
          const cm = colorMap[gw.color];
          return (
            <motion.div key={gw.id} variants={fadeUp} custom={i + 1}>
              <GlassCard isDark={isDark} hover={false} className="relative overflow-hidden">
                {/* Gradient accent */}
                <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20 bg-gradient-to-br ${cm.gradient}`} />

                {/* Header */}
                <div className="flex items-center justify-between mb-6 relative">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${cm.bg} border ${cm.border}`}>
                      <gw.icon className={`w-5 h-5 ${cm.text}`} />
                    </div>
                    <div>
                      <h3 className={`text-base font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{gw.label}</h3>
                      <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{gw.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusDot active={enabledGateways[gw.key]} isDark={isDark} />
                    <ToggleSwitch
                      enabled={enabledGateways[gw.key]}
                      onToggle={() => setEnabledGateways(p => ({ ...p, [gw.key]: !p[gw.key] }))}
                      isDark={isDark}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="relative">
                  {gw.content}
                </div>
              </GlassCard>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

// TAB: Data Entry (WOW Factor)
function DataEntryTab({ isDark, card, inputCl, textP, textS }) {
  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '', company: '', website: '',
    category: '', description: '', startDate: '', budget: '',
    priority: 'medium', attachments: [], notes: '',
  });
  const [expandedSections, setExpandedSections] = useState({ personal: true, project: true, additional: false });
  const [focusedField, setFocusedField] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(p => ({ ...p, [section]: !p[section] }));
  };

  const validateField = (key, value) => {
    if (key === 'email' && value && !/\S+@\S+\.\S+/.test(value)) {
      setErrors(p => ({ ...p, email: 'بريد إلكتروني غير صالح' }));
      return false;
    }
    setErrors(p => { const n = { ...p }; delete n[key]; return n; });
    return true;
  };

  const handleSubmit = () => {
    let valid = true;
    if (!formData.fullName) { setErrors(p => ({ ...p, fullName: 'الاسم مطلوب' })); valid = false; }
    if (!formData.email) { setErrors(p => ({ ...p, email: 'البريد مطلوب' })); valid = false; }
    if (!valid) { toast.error('يرجى ملء الحقول المطلوبة'); return; }
    toast.success('تم حفظ البيانات بنجاح!');
  };

  const FloatingInput = ({ label, icon: Icon, value, onChange, type = 'text', error, fieldKey, placeholder, ...props }) => {
    const isFocused = focusedField === fieldKey;
    const hasValue = value && value.length > 0;

    return (
      <motion.div layout className="relative group">
        {/* Gradient border effect */}
        <div className={`absolute -inset-[1px] rounded-xl transition-all duration-300 ${
          isFocused
            ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 opacity-100'
            : error ? 'bg-gradient-to-r from-red-500 to-red-400 opacity-100' : 'opacity-0'
        }`} style={{ borderRadius: '0.8rem' }} />

        <div className={`relative rounded-xl transition-all duration-200 ${
          isDark
            ? 'bg-[#0A0A0C]/80 backdrop-blur-xl'
            : 'bg-white'
        }`}>
          <div className="relative">
            {/* Floating Label */}
            <motion.label
              animate={{
                y: isFocused || hasValue ? -10 : 0,
                scale: isFocused || hasValue ? 0.8 : 1,
                color: isFocused ? '#3B82F6' : error ? '#EF4444' : isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
              }}
              transition={{ duration: 0.15 }}
              className={`absolute top-1/2 right-12 -translate-y-1/2 text-sm pointer-events-none origin-right z-10`}
            >
              {label}
            </motion.label>

            <Input
              type={type}
              value={value}
              onChange={e => { onChange(e); validateField(fieldKey, e.target.value); }}
              onFocus={() => setFocusedField(fieldKey)}
              onBlur={() => setFocusedField(null)}
              placeholder={isFocused ? placeholder : ''}
              className={`rounded-xl h-12 pr-12 ${isDark ? 'bg-transparent border-white/[0.06] text-white placeholder:text-white/15' : 'bg-transparent border-gray-200 text-gray-900 placeholder:text-gray-300'} ${error ? 'border-red-500/50' : ''}`}
              {...props}
            />

            {/* Icon */}
            <div className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isFocused ? 'text-blue-500' : isDark ? 'text-white/15' : 'text-gray-300'}`}>
              <Icon className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Error message with shake */}
        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: [10, -5, 5, -3, 0] }}
              exit={{ opacity: 0 }}
              className="text-xs text-red-500 mt-1 pr-1"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const sections = [
    {
      id: 'personal',
      title: 'المعلومات الشخصية',
      subtitle: 'البيانات الأساسية للمستخدم',
      icon: Users,
      fields: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FloatingInput label="الاسم الكامل" icon={Users} value={formData.fullName} onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))} fieldKey="fullName" error={errors.fullName} placeholder="أحمد محمد" />
          <FloatingInput label="البريد الإلكتروني" icon={Mail} value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} type="email" fieldKey="email" error={errors.email} placeholder="user@example.com" />
          <FloatingInput label="رقم الهاتف" icon={Smartphone} value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} fieldKey="phone" placeholder="+966 5xxxxxxxx" />
          <FloatingInput label="الشركة" icon={Building2} value={formData.company} onChange={e => setFormData(p => ({ ...p, company: e.target.value }))} fieldKey="company" placeholder="اسم الشركة" />
          <div className="md:col-span-2">
            <FloatingInput label="الموقع الإلكتروني" icon={Globe} value={formData.website} onChange={e => setFormData(p => ({ ...p, website: e.target.value }))} fieldKey="website" placeholder="https://example.com" />
          </div>
        </div>
      ),
    },
    {
      id: 'project',
      title: 'تفاصيل المشروع',
      subtitle: 'معلومات تفصيلية عن المشروع',
      icon: Layers,
      fields: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Select with Search */}
            <div className="relative group">
              <div className={`absolute -inset-[1px] rounded-xl transition-all duration-300 ${focusedField === 'category' ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 opacity-100' : 'opacity-0'}`} style={{ borderRadius: '0.8rem' }} />
              <div className={`relative rounded-xl ${isDark ? 'bg-[#0A0A0C]/80 backdrop-blur-xl' : 'bg-white'}`}>
                <select
                  value={formData.category}
                  onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
                  onFocus={() => setFocusedField('category')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full h-12 rounded-xl border px-4 pr-12 text-sm appearance-none ${isDark ? 'bg-transparent border-white/[0.06] text-white' : 'bg-transparent border-gray-200 text-gray-900'}`}
                >
                  <option value="">اختر التصنيف...</option>
                  <option value="tech">تقنية</option>
                  <option value="business">أعمال</option>
                  <option value="design">تصميم</option>
                  <option value="marketing">تسويق</option>
                  <option value="education">تعليم</option>
                  <option value="other">أخرى</option>
                </select>
                <SlidersHorizontal className={`absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${focusedField === 'category' ? 'text-blue-500' : isDark ? 'text-white/15' : 'text-gray-300'}`} />
              </div>
            </div>

            <FloatingInput label="تاريخ البدء" icon={Calendar} value={formData.startDate} onChange={e => setFormData(p => ({ ...p, startDate: e.target.value }))} type="date" fieldKey="startDate" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FloatingInput label="الميزانية ($)" icon={DollarSign} value={formData.budget} onChange={e => setFormData(p => ({ ...p, budget: e.target.value }))} type="number" fieldKey="budget" placeholder="5000" />

            {/* Priority Select */}
            <div>
              <p className={`text-xs font-medium mb-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>الأولوية</p>
              <div className="flex gap-2">
                {[
                  { value: 'low', label: 'منخفضة', color: 'emerald' },
                  { value: 'medium', label: 'متوسطة', color: 'amber' },
                  { value: 'high', label: 'عالية', color: 'red' },
                ].map(p => (
                  <motion.button
                    key={p.value}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setFormData(prev => ({ ...prev, priority: p.value }))}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all border ${
                      formData.priority === p.value
                        ? isDark
                          ? `bg-${p.color}-500/10 border-${p.color}-500/30 text-${p.color}-400`
                          : `bg-${p.color}-50 border-${p.color}-300 text-${p.color}-600`
                        : isDark
                          ? 'bg-white/[0.02] border-white/[0.04] text-white/30 hover:border-white/[0.1]'
                          : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    {p.label}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>

          {/* Description Textarea */}
          <div className="relative group">
            <div className={`absolute -inset-[1px] rounded-xl transition-all duration-300 ${focusedField === 'description' ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 opacity-100' : 'opacity-0'}`} style={{ borderRadius: '0.8rem' }} />
            <div className={`relative rounded-xl ${isDark ? 'bg-[#0A0A0C]/80 backdrop-blur-xl' : 'bg-white'}`}>
              <Textarea
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                onFocus={() => setFocusedField('description')}
                onBlur={() => setFocusedField(null)}
                rows={4}
                placeholder="اكتب وصفاً تفصيلياً للمشروع..."
                className={`rounded-xl text-sm ${isDark ? 'bg-transparent border-white/[0.06] text-white placeholder:text-white/15' : 'bg-transparent border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
              />
            </div>
          </div>

          {/* File Upload Area */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            className={`relative rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-all ${
              isDark ? 'border-white/[0.06] hover:border-blue-500/30 bg-white/[0.01]' : 'border-gray-200 hover:border-blue-400 bg-gray-50/50'
            }`}
          >
            <div className={`inline-flex p-3 rounded-xl mb-3 ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
              <FileUp className="w-6 h-6 text-blue-500" />
            </div>
            <p className={`text-sm font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`}>اسحب الملفات هنا أو انقر للتحميل</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-white/20' : 'text-gray-400'}`}>PNG, JPG, PDF حتى 10MB</p>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <motion.div className="mt-4 mx-auto max-w-xs">
                <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.04]' : 'bg-gray-200'}`}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                  />
                </div>
                <p className={`text-[10px] mt-1 ${isDark ? 'text-white/20' : 'text-gray-400'}`}>{uploadProgress}%</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      ),
    },
    {
      id: 'additional',
      title: 'ملاحظات إضافية',
      subtitle: 'معلومات تكميلية اختيارية',
      icon: FileText,
      fields: (
        <div className="space-y-4">
          <div className="relative group">
            <div className={`absolute -inset-[1px] rounded-xl transition-all duration-300 ${focusedField === 'notes' ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 opacity-100' : 'opacity-0'}`} style={{ borderRadius: '0.8rem' }} />
            <div className={`relative rounded-xl ${isDark ? 'bg-[#0A0A0C]/80 backdrop-blur-xl' : 'bg-white'}`}>
              <Textarea
                value={formData.notes}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                onFocus={() => setFocusedField('notes')}
                onBlur={() => setFocusedField(null)}
                rows={6}
                placeholder="أضف أي ملاحظات إضافية هنا..."
                className={`rounded-xl text-sm ${isDark ? 'bg-transparent border-white/[0.06] text-white placeholder:text-white/15' : 'bg-transparent border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
              />
            </div>
          </div>
          <p className={`text-xs ${isDark ? 'text-white/15' : 'text-gray-400'}`}>يدعم النص العادي. للتنسيق المتقدم يمكنك استخدام Markdown.</p>
        </div>
      ),
    },
  ];

  return (
    <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-6">
      {/* Header with Preview Toggle */}
      <motion.div variants={fadeUp} className="flex items-center justify-between">
        <SectionTitle icon={Database} title="نموذج البيانات" subtitle="نموذج إدخال بيانات متقدم بتصميم احترافي" isDark={isDark} />
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            onClick={() => setPreviewMode(!previewMode)}
            className={`rounded-xl h-9 px-4 text-sm ${
              previewMode
                ? isDark ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white'
                : isDark ? 'bg-white/[0.04] hover:bg-white/[0.08] text-white/60 border border-white/[0.06]' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'
            }`}
          >
            {previewMode ? <Eye className="w-3.5 h-3.5 ml-2" /> : <EyeOff className="w-3.5 h-3.5 ml-2" />}
            {previewMode ? 'وضع المعاينة' : 'وضع التحرير'}
          </Button>
        </motion.div>
      </motion.div>

      <AnimatePresence mode="wait">
        {previewMode ? (
          /* Preview Mode */
          <motion.div key="preview" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            <GlassCard isDark={isDark}>
              <div className="flex items-center gap-3 mb-6">
                <div className={`p-2 rounded-xl ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'}`}>
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
                <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>معاينة البيانات المدخلة</h3>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'الاسم الكامل', value: formData.fullName },
                  { label: 'البريد الإلكتروني', value: formData.email },
                  { label: 'رقم الهاتف', value: formData.phone },
                  { label: 'الشركة', value: formData.company },
                  { label: 'الموقع', value: formData.website },
                  { label: 'التصنيف', value: formData.category },
                  { label: 'الميزانية', value: formData.budget ? `$${formData.budget}` : '' },
                  { label: 'تاريخ البدء', value: formData.startDate },
                  { label: 'الأولوية', value: { low: 'منخفضة', medium: 'متوسطة', high: 'عالية' }[formData.priority] },
                  { label: 'الوصف', value: formData.description },
                  { label: 'ملاحظات', value: formData.notes },
                ].filter(item => item.value).map((item, i) => (
                  <motion.div key={item.label} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                    className={`flex items-start gap-4 p-3 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                    <span className={`text-xs font-medium w-32 flex-shrink-0 pt-0.5 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{item.label}</span>
                    <span className={`text-sm ${textP}`}>{item.value}</span>
                  </motion.div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        ) : (
          /* Edit Mode */
          <motion.div key="edit" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}
            className="space-y-4">
            {sections.map((section, sIdx) => (
              <motion.div key={section.id} variants={fadeUp} custom={sIdx} layout>
                <GlassCard isDark={isDark} hover={false} className="overflow-hidden !p-0">
                  {/* Section Header */}
                  <motion.div
                    whileHover={{ backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}
                    className="flex items-center justify-between p-5 cursor-pointer"
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-xl ${isDark ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-white/[0.04]' : 'bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-100'}`}>
                        <section.icon className="w-4 h-4 text-blue-500" />
                      </div>
                      <div>
                        <h4 className={`text-sm font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{section.title}</h4>
                        <p className={`text-xs ${isDark ? 'text-white/25' : 'text-gray-400'}`}>{section.subtitle}</p>
                      </div>
                    </div>
                    <motion.div animate={{ rotate: expandedSections[section.id] ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronDown className={`w-4 h-4 ${isDark ? 'text-white/20' : 'text-gray-400'}`} />
                    </motion.div>
                  </motion.div>

                  {/* Section Content */}
                  <AnimatePresence>
                    {expandedSections[section.id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className={`p-5 pt-2 border-t ${isDark ? 'border-white/[0.03]' : 'border-gray-100'}`}>
                          {section.fields}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </GlassCard>
              </motion.div>
            ))}

            {/* Submit Button */}
            <motion.div variants={fadeUp} custom={sections.length} className="flex justify-end">
              <SaveButton onClick={handleSubmit} loading={false} isDark={isDark} label="حفظ جميع البيانات" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function SettingsPage() {
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';

  // Style tokens
  const card = isDark ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]' : 'bg-white border border-gray-200 shadow-sm';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-500';
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.06] text-white placeholder:text-white/20' : 'bg-gray-50 border-gray-200 text-gray-900';

  const commonProps = { isDark, card, inputCl, textP, textS };

  const tabItems = [
    { value: 'general', icon: Settings, label: 'عام' },
    { value: 'branding', icon: Palette, label: 'العلامة التجارية' },
    { value: 'affiliate', icon: Percent, label: 'الأفليت' },
    { value: 'payments', icon: CreditCard, label: 'الدفع' },
    { value: 'security', icon: Shield, label: 'الأمان' },
    { value: 'pages', icon: FileText, label: 'الصفحات' },
    { value: 'facebook', icon: Video, label: 'فيسبوك' },
    { value: 'users', icon: Users, label: 'المستخدمين' },
    { value: 'authorities', icon: Building2, label: 'الجهات المختصة' },
    { value: 'activity', icon: Activity, label: 'سجل النشاطات' },
    { value: 'payment-advanced', icon: Banknote, label: 'الدفع المتقدم' },
    { value: 'data-entry', icon: Database, label: 'نموذج البيانات' },
  ];

  return (
    <div className={`min-h-screen pt-20 pb-16 px-4 sm:px-6 ${isDark ? 'bg-[#020204]' : 'bg-gray-50/80'}`} dir="rtl">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <motion.div
              whileHover={{ rotate: 90 }}
              transition={{ duration: 0.3 }}
              className={`p-3 rounded-2xl ${isDark ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/[0.06]' : 'bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-100'}`}
            >
              <Settings className="w-6 h-6 text-blue-500" />
            </motion.div>
            <div>
              <h1 className={`text-2xl sm:text-3xl font-bold tracking-tight ${textP}`} style={{ fontFamily: 'Outfit' }}>
                إعدادات النظام
              </h1>
              <p className={`text-sm mt-0.5 ${textS}`}>إدارة تكوين المنصة والعلامة التجارية والأمان</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="general" className="w-full">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1}>
            <TabsList className={`w-full flex flex-wrap gap-1 h-auto p-1.5 rounded-2xl mb-8 ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-100/80 border border-gray-200'}`}>
              {tabItems.map(tab => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-all data-[state=active]:shadow-md ${
                    isDark
                      ? 'data-[state=active]:bg-white/[0.08] data-[state=active]:text-white text-white/40 hover:text-white/60'
                      : 'data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </motion.div>

          <TabsContent value="general"><GeneralTab {...commonProps} /></TabsContent>
          <TabsContent value="branding"><BrandingTab {...commonProps} /></TabsContent>
          <TabsContent value="affiliate"><AffiliateTab {...commonProps} /></TabsContent>
          <TabsContent value="payments"><PaymentsTab {...commonProps} /></TabsContent>
          <TabsContent value="security"><SecurityTab {...commonProps} /></TabsContent>
          <TabsContent value="pages"><PagesTab {...commonProps} /></TabsContent>
          <TabsContent value="facebook"><FacebookTab {...commonProps} /></TabsContent>
          <TabsContent value="users"><UsersTab {...commonProps} /></TabsContent>
          <TabsContent value="authorities"><AuthoritiesTab {...commonProps} /></TabsContent>
          <TabsContent value="activity"><ActivityTab {...commonProps} /></TabsContent>
          <TabsContent value="payment-advanced"><PaymentAdvancedTab {...commonProps} /></TabsContent>
          <TabsContent value="data-entry"><DataEntryTab {...commonProps} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
